import {
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    BellIcon,
    ChevronDownIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const Header = ({ sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  // Real platform figures. These were previously hardcoded to invented
  // numbers, which meant every page showed statistics that were not true.
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/admin/analytics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // The endpoint nests the figures under `overview`.
        setStats(res.data?.data?.overview || null);
      } catch (error) {
        // Leave the figures blank rather than showing something invented.
        setStats(null);
      }
    };
    loadStats();
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('adminUser');
    if (userStr) {
      try {
        setAdminUser(JSON.parse(userStr));
      } catch (error) {
        console.error('Error parsing admin user:', error);
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const resolveSearchRoute = (query = '') => {
    const normalized = String(query || '').trim().toLowerCase();
    if (!normalized) return null;

    if (normalized.includes('seller') && normalized.includes('setting')) {
      return '/settings/seller-app';
    }

    if (normalized.includes('buyer') && normalized.includes('setting')) {
      return '/settings/buyer-app';
    }

    if (['dashboard', 'home', 'overview'].some((word) => normalized.includes(word))) {
      return '/';
    }

    if (['agent', 'agents'].some((word) => normalized.includes(word))) {
      return '/agents';
    }

    if (['car', 'cars', 'listing', 'listings'].some((word) => normalized.includes(word))) {
      return '/cars';
    }

    if (['seller', 'sellers'].some((word) => normalized.includes(word))) {
      return '/sellers';
    }

    if (['buyer', 'buyers', 'customer', 'customers'].some((word) => normalized.includes(word))) {
      return '/buyers';
    }

    if (['membership', 'memberships', 'plan', 'plans'].some((word) => normalized.includes(word))) {
      return '/memberships';
    }

    if (['analytics', 'report', 'reports'].some((word) => normalized.includes(word))) {
      return '/analytics';
    }

    if (['app', 'apps', 'ad', 'ads'].some((word) => normalized.includes(word))) {
      return '/app-management';
    }

    return null;
  };

  const handleHeaderSearchSubmit = () => {
    const route = resolveSearchRoute(headerSearchQuery);

    if (!route) {
      toast('No matching section. Try: cars, agents, sellers, buyers, memberships, analytics.');
      return;
    }

    navigate(route);
    setHeaderSearchQuery('');
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleHeaderSearchSubmit();
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left section */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors lg:hidden"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* Search */}
          <div className="relative hidden md:block">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search pages: cars, sellers, agents..."
              value={headerSearchQuery}
              onChange={(event) => setHeaderSearchQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="block w-96 pl-10 pr-20 py-2 border border-gray-200 rounded-xl leading-5 bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 sm:text-sm transition-all duration-200"
            />
            <button
              type="button"
              onClick={handleHeaderSearchSubmit}
              className="absolute inset-y-1 right-1 px-3 text-xs font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
            >
              Go
            </button>
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center space-x-4">
          {/* Quick Stats */}
          <div className="hidden lg:flex items-center space-x-6 px-6 py-2 bg-gray-50 rounded-xl">
            <div className="text-center">
              <div className="text-lg font-bold text-primary-600">
                {stats ? Number(stats.totalAgents ?? 0).toLocaleString('en-IN') : '—'}
              </div>
              <div className="text-xs text-gray-500">Agents</div>
            </div>
            <div className="w-px h-8 bg-gray-300"></div>
            <div className="text-center">
              <div className="text-lg font-bold text-secondary-600">
                {stats ? Number(stats.totalCars ?? 0).toLocaleString('en-IN') : '—'}
              </div>
              <div className="text-xs text-gray-500">Listed Cars</div>
            </div>
            <div className="w-px h-8 bg-gray-300"></div>
            <div className="text-center">
              <div className="text-lg font-bold text-success-600">
                {stats ? `₹${Number(stats.monthlyRevenue ?? 0).toLocaleString('en-IN')}` : '—'}
              </div>
              <div className="text-xs text-gray-500">Revenue</div>
            </div>
          </div>

          {/* Notifications */}
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-400 hover:text-gray-500 rounded-xl hover:bg-gray-100 transition-colors relative"
            >
              <BellIcon className="w-6 h-6" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-danger-500 rounded-full text-xs text-white flex items-center justify-center font-medium">
                3
              </span>
            </motion.button>
          </div>

          {/* Profile dropdown */}
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center space-x-3 px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div className="w-10 h-10 bg-gradient-orange rounded-xl flex items-center justify-center shadow-orange">
                <span className="text-white font-bold text-sm">
                  {adminUser?.firstName?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-gray-900">
                  {adminUser?.firstName && adminUser?.lastName 
                    ? `${adminUser.firstName} ${adminUser.lastName}` 
                    : 'Admin User'}
                </div>
                <div className="text-xs text-gray-500">Super Administrator</div>
              </div>
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            </motion.button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {showDropdown && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowDropdown(false)}
                  />
                  
                  {/* Dropdown */}
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20"
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">
                        {adminUser?.email || 'admin@usedadda.com'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Role: {adminUser?.role || 'Admin'}
                      </div>
                    </div>
                    
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors mt-1"
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5" />
                      Sign Out
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile search */}
      <div className="mt-4 md:hidden">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search pages..."
            value={headerSearchQuery}
            onChange={(event) => setHeaderSearchQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="block w-full pl-10 pr-20 py-2 border border-gray-200 rounded-xl leading-5 bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 sm:text-sm transition-all duration-200"
          />
          <button
            type="button"
            onClick={handleHeaderSearchSubmit}
            className="absolute inset-y-1 right-1 px-3 text-xs font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
          >
            Go
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;